import { Mail, ClipboardList } from "lucide-react";
import { MaskedLines, Reveal } from "@/components/motion";
import RequestWizard from "@/components/RequestWizard";
import { EMAIL } from "@/constants/site";

const CHECKLIST = [
  "Project state and location",
  "Current architectural or floor plan",
  "Equipment manufacturer and exact model",
  "Manufacturer planning or installation drawings, when available",
  "Expected clinical use or workload",
  "Known wall, floor, ceiling, door, and window construction",
  "Occupancy around the room — including spaces above and below",
];

const ASSURANCES = [
  { n: "01", t: "Response in one business day", b: "Complete project information in, confirmation and next steps out." },
  { n: "02", t: "Report in five business days", b: "Typical turnaround once complete information is received." },
  { n: "03", t: "90-day money-back", b: "A full refund for any reason within 90 days of delivery." },
  { n: "04", t: "Five years of recalculations", b: "Unlimited recalculations if the machine, workload, or room changes." },
];

const Request = () => (
  <>
    <section data-testid="request-hero" className="border-b border-border bg-blueprint-grid pt-16">
      <div className="mx-auto max-w-7xl px-5 pb-14 pt-14 md:px-8 md:pb-20 md:pt-20">
        <Reveal y={12}>
          <p className="eyebrow">Primary next step — Project intake</p>
        </Reveal>
        <h1 className="mt-6 max-w-3xl font-heading text-4xl font-extrabold leading-[1.05] tracking-tight text-ink sm:text-5xl lg:text-6xl">
          <MaskedLines delay={0.1} lines={["Request a", "shielding design."]} />
        </h1>
        <Reveal delay={0.5}>
          <p className="mt-7 max-w-2xl text-base leading-relaxed text-ink/70 md:text-lg" data-testid="request-intro">
            Tell us about your facility, choose your rooms, and prepay securely — then complete the per-room details and floor plans. Heartland Physics responds within one business day of your completed intake.
          </p>
        </Reveal>
      </div>
    </section>

    <section className="border-b border-border bg-blueprint-grid">
      <div className="mx-auto grid max-w-7xl gap-10 px-5 py-14 md:px-8 md:py-20 lg:grid-cols-[1fr_340px]">
        <Reveal delay={0.1}>
          <RequestWizard />
        </Reveal>

        <div className="flex flex-col gap-8">
          <Reveal delay={0.15}>
            <div className="border border-border bg-white p-6">
              <div className="flex items-center gap-3">
                <ClipboardList size={17} className="text-blueprint" />
                <h2 className="font-heading text-sm font-extrabold tracking-tight text-ink">What to have ready</h2>
              </div>
              <div className="mt-5 flex flex-col gap-3">
                {CHECKLIST.map((item, i) => (
                  <div key={item} className="flex items-start gap-3" data-testid={`checklist-item-${i}`}>
                    <span className="mt-0.5 font-mono text-xs text-blueprint">{String(i + 1).padStart(2, "0")}</span>
                    <p className="text-xs leading-relaxed text-ink/70">{item}</p>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="border border-border bg-white p-6" data-testid="request-alternates">
              <p className="font-mono text-xs uppercase tracking-[0.24em] text-ink/50">Prefer another route?</p>
              <div className="mt-4 flex flex-col gap-3">
                <a href={`mailto:${EMAIL}`} data-testid="request-alt-email" className="group flex items-center gap-2 text-sm font-semibold text-blueprint transition-colors hover:text-ink">
                  <Mail size={15} /> Email {EMAIL}
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>

    <section className="bg-ink text-white">
      <div className="bg-blueprint-grid-dark">
        <div className="mx-auto grid max-w-7xl gap-8 px-5 py-14 sm:grid-cols-2 md:px-8 md:py-16 lg:grid-cols-4">
          {ASSURANCES.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.08}>
              <p className="font-mono text-xs text-azure">{s.n}</p>
              <h3 className="mt-3 font-heading text-base font-bold tracking-tight">{s.t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-white/60">{s.b}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  </>
);

export default Request;
